// it could be your App.tsx file or theme file that is included in your tsconfig.json
import { Theme, createTheme } from '@mui/material/styles';
import { DEFAULT_TIME_STEPS, DEFAULT_MINUTES_STEP } from './components/timePickerDefaults';

declare module '@mui/material/styles' {
    interface Theme {
        cp: {
            contentHeight: string;
        };
    }
    // allow configuration using `createTheme`
    interface ThemeOptions {
        cp?: {
            contentHeight?: string;
        };
    }
}

declare module '@mui/styles/defaultTheme' {
    // aaa eslint-disable-next-line @typescript-eslint/no-empty-interface (remove this line if you don't have the rule enabled)
    interface DefaultTheme extends Theme {}
}

// Dark mode is currently scoped to the chat pages (see chat/ChatThemeScope.js);
// the rest of the dashboard always renders with the light theme.
// 'dim' is a lower-contrast dark theme (muted grey surfaces, softer text).
export type ThemeMode = 'light' | 'dark' | 'dim';

const darkPalette = {
    background: {
        default: '#15171b',
        paper: '#1e2126',
    },
    text: {
        primary: '#e7e9ed',
        secondary: '#b3b8c2',
        disabled: 'rgba(255, 255, 255, 0.3)',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
};

const dimPalette = {
    primary: {
        main: '#5b8fd0',
        contrastText: '#fff',
    },
    background: {
        default: '#2b2e34',
        paper: '#33363d',
    },
    text: {
        primary: '#c5cad3',
        secondary: '#a2a8b2',
        disabled: 'rgba(255, 255, 255, 0.26)',
    },
    divider: 'rgba(255, 255, 255, 0.09)',
};

const modePalettes = { light: {}, dark: darkPalette, dim: dimPalette };

export const buildTheme = (mode: ThemeMode = 'light') => createTheme({
    palette: {
        mode: mode === 'light' ? 'light' : 'dark',
        primary: {
            main: '#007AFF',
        },
        secondary: {
            main: '#fa3f33',
        },
        warning: {
            main: '#edc001',
        },
        ...modePalettes[mode],
    },
    cp: {
        //Device screen height minus the appBar component(src/layout/main.js) height
        contentHeight: 'calc(100svh - 4.1rem)',
    },

    components: {
        MuiTimePicker: {
            defaultProps: {
                minutesStep: DEFAULT_MINUTES_STEP,
                timeSteps: DEFAULT_TIME_STEPS,
            },
        },
        MuiDesktopTimePicker: {
            defaultProps: {
                minutesStep: DEFAULT_MINUTES_STEP,
                timeSteps: DEFAULT_TIME_STEPS,
            },
        },
        MuiMobileTimePicker: {
            defaultProps: {
                minutesStep: DEFAULT_MINUTES_STEP,
                timeSteps: DEFAULT_TIME_STEPS,
            },
        },
        MuiStaticTimePicker: {
            defaultProps: {
                minutesStep: DEFAULT_MINUTES_STEP,
                timeSteps: DEFAULT_TIME_STEPS,
            },
        },
        MuiMultiSectionDigitalClock: {
            defaultProps: {
                minutesStep: DEFAULT_MINUTES_STEP,
                timeSteps: DEFAULT_TIME_STEPS,
            },
        },
        MuiDigitalClock: {
            defaultProps: {
                minutesStep: DEFAULT_MINUTES_STEP,
                timeStep: DEFAULT_MINUTES_STEP,
            },
        },
        MuiDateTimePicker: {
            defaultProps: {
                minutesStep: DEFAULT_MINUTES_STEP,
                timeSteps: DEFAULT_TIME_STEPS,
            },
        },
        MuiDesktopDateTimePicker: {
            defaultProps: {
                minutesStep: DEFAULT_MINUTES_STEP,
                timeSteps: DEFAULT_TIME_STEPS,
            },
        },
        MuiMobileDateTimePicker: {
            defaultProps: {
                minutesStep: DEFAULT_MINUTES_STEP,
                timeSteps: DEFAULT_TIME_STEPS,
            },
        },
        // For most of them you will need to adjust just the root...
        // Filled fields per Martina's design: rounded on all four corners, with a
        // thin border in place of MUI's square-ended underline. Lives on FilledInput
        // (not TextField) so filled Selects and Autocompletes pick it up too.
        MuiFilledInput: {
            defaultProps: {
                disableUnderline: true,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: 8,
                    borderBottom: '1px solid #747475',
                    // The underline was the only focus/error cue, so carry them on the border
                    '&.Mui-focused': {
                        borderBottomColor: theme.palette.primary.main,
                    },
                    '&.Mui-error': {
                        borderBottomColor: theme.palette.error.main,
                    },
                    '&.Mui-disabled': {
                        borderBottomStyle: 'dotted',
                    },
                }),
            },
        },
    },
});

const theme = buildTheme('light');

export default theme;
